import "./index.css";
import LoginPage from "LoginPage";
import Signup from "signup/signup";
import { message, Modal } from "antd";
import React, { useEffect, useRef } from "react";
import { useTrackEvent } from "../../analytics/useTrackEvent";

interface TypeSignProp<T = "SIGNUP" | "SIGNIN"> {
  /**
   * @param openType SIGNUP SIGNIN
   * @description 为空则关闭Modal
   */
  openType: T;
  /**
   *
   * @name onOpenTypeChange 点击 “Sign In”、“Sign Up”
   * @value SIGNUP SIGNIN 为空则关闭Modal
   */
  onOpenTypeChange(value?: T): Promise<void | Record<string, string>>;
  /**
   * @param email 用户输入的邮箱
   * @description 邮箱直接发起注册流程
   */
  join?: {
    /**
     * @param email
     */
    email?: string;
    /**
     * @param fillIn 跳过邮箱验证流程
     */
    fillIn?: boolean;
  };
}

/**
 * @name Sign 登陆、注册
 * @description 引入的就是平台登陆注册组件，所以这里登陆跟平台保持一致
 */
const Sign: React.FC<TypeSignProp> = ({ join, openType, onOpenTypeChange }) => {
  const signinRef =
    useRef<Record<"onFinish" | "onRegisterEmail", (obj: object) => void>>();
  const { trackSaspUserSignedUp, trackSaspUserSignedIn } = useTrackEvent();

  useEffect(() => {
    if (openType === "SIGNUP") {
      if (!join?.email) return;
      if (join?.fillIn) {
        // 跳过验证码流程，直接填写关键数据，这个是Google、Outlook 引导注册而来的
        setTimeout(() => {
          signinRef.current.onRegisterEmail({ emailaddress: join?.email });
        });
      } else {
        // 需要验证码流程
        setTimeout(() => {
          signinRef.current.onFinish({ emailaddress: join?.email });
        });
      }
    }
  }, [openType, join]);

  async function onSigninSuccess() {
    message.success("Account login successfully!");
    const user = await onOpenTypeChange();
    // Track user signed in event for standalone page
    if (user && user.ID) {
      trackSaspUserSignedIn(user.ID);
    }
  }

  async function onSignupSuccess() {
    message.success("Account created successfully!");
    const user = await onOpenTypeChange();
    // Track user signed up event for standalone page
    if (user && user.ID) {
      trackSaspUserSignedUp(user.ID);
    }
  }

  // 用户引导用户注册，[不需要填写email的确认code]
  function onSignUp(email?: string) {
    onOpenTypeChange("SIGNUP");
    if (email) {
      setTimeout(() => {
        signinRef.current.onRegisterEmail({ emailaddress: email });
      });
    }
  }

  return (
    <Modal
      centered
      zIndex={9999}
      footer={null}
      width="1040px"
      destroyOnClose
      className="ai-sign"
      maskClosable={false}
      open={Boolean(openType)}
      onCancel={() => onOpenTypeChange()}
    >
      {openType === "SIGNIN" ? (
        <LoginPage
          useEmailCode
          onToSignUp={onSignUp}
          onSuccess={onSigninSuccess}
          banner="/lender_icon/iframe_baner.png"
        />
      ) : (
        <Signup
          ref={signinRef}
          onSuccess={onSignupSuccess}
          banner="/lender_icon/iframe_baner.png"
          onToSignIn={() => onOpenTypeChange("SIGNIN")}
        />
      )}
    </Modal>
  );
};

export default Sign;
